import ProductQtyInput from '@/components/Product/ProductQtyInput'
import React, { useContext } from 'react'
import { toast } from 'react-toastify'
import { useIntl } from 'react-intl'

import Paper from '@mui/material/Paper'
import MenuItem from '@mui/material/MenuItem'
import { CircularProgress, Select, Skeleton } from '@mui/material'
import Grid from '@mui/material/Grid'
import Tooltip from '@mui/material/Tooltip'

import Link from '@/components/Common/Link'
import { useStyleProduct } from '@/components/Product/Product'
import TableResponsive from '@/components/Common/TableResponsive'
import ProductImg, { sizeMinImageProduct } from '@/components/Product/ProductImg'
import { useConfirm } from 'material-ui-confirm'
import { useBasketLocked } from '@/components/Basket/BasketLockedContext'

import getRoutes from '@/lib/getRoutes'
import getProductDeclination from '@/lib/getProductDeclination'
import logger from '@/lib/logger'

import COLORS from '@/src/colors'
import { useQueryClient } from '@tanstack/react-query'
import { useRestMutation } from '@/lib/hook/tanstackQuery'
import { ApiQueryEndpoint } from '@/src/ApiQueryEndpoint'
import { ApiMutationEndpoint } from '@/src/ApiMutationEndpoint'
import { removeDisplayFraisDePrepraProduct } from '@/lib/basket/rulesPreparationCosts'
import { ProductsBasket } from '@/type/basket'
import SHIPPING_MANAGER from '@/lib/basket/SHIPPING_MANAGER'
import { gaAddToCart } from '@/lib/googleAnalytics/actions'
import { debounce } from 'lodash'
import Button from '@/components/Common/Button'
import BasketFrancoSuggestions from '@/components/Basket/BasketFrancoSuggestions'
import BasketProductPlanningButton from '@/components/Basket/BasketProductPlanningButton'
import ProfileContext from '@/components/Context/ProfileContext'

import MainLoader from '../MainLoader'
import FrancoProgress from '../Common/Header/FrancoProgress'
import CompanyName from '@/components/CompanyName'

import MinOrderPriceProgress from '@/components/Common/MinOrderPriceProgress'
import BrandNameLink from '@/components/Brands/BrandNameLink'
import useBasketHelper from '@/lib/hook/useBasketHelper'
import clsx from 'clsx'
import Card from '../Common/Card'
import { DeleteForeverOutlined } from '@mui/icons-material'
import CartSaveForLater from '@/components/Basket/CartSaveForLater'
import OpenInFullIcon from '@mui/icons-material/OpenInFull'
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen'

type Props = {
  group: any
  compactMode?: boolean
}

export const dataShippingFranco = (group, shippingGroups) => {
  const shippingSelected =
    shippingGroups?.find(ship => ship.group_key === group.group_key) || group.shippings?.[0]

  const totalPrice = group.products
    .filter(removeDisplayFraisDePrepraProduct)
    .reduce((previous, current) => previous + Number(current.subtotal), 0)
    .toFixed(2)

  const totalPriceWithoutDiscountCode = group.products
    .filter(removeDisplayFraisDePrepraProduct)
    .reduce((previous, current) => previous + Number(current.original_price) * current.amount, 0)
    .toFixed(2)

  const rateInfo = group.shippings.find(
    g => g.shipping_id === shippingSelected.shipping_id
  )?.rate_info
  const rangeFree = rateInfo?.rate_value.find(
    item => Number(item.value) + Number(rateInfo.base_rate) === 0
  )
  let maxRange = 0
  rateInfo?.rate_value.forEach(r => {
    if (maxRange <= r.range_to_value) maxRange = r.range_to_value
  })

  // Si il y a un tarif de base et qu'on a pas spécifié volontairement de range gratuite, on désactive les franco
  const amountFranco = rangeFree
    ? rangeFree?.range_from_value
    : rateInfo?.base_rate === 0
      ? maxRange
      : 0

  return { totalPrice, totalPriceWithoutDiscountCode, amountFranco, shippingSelected }
}

const BasketDetailCompany: React.FC<Props> = ({
  group,
  compactMode: compactModeParent = false,
}) => {
  const [compactMode, setLocalCompactMode] = React.useState(compactModeParent)

  const confirm = useConfirm()
  const classesProduct = useStyleProduct({})
  const { showLocked } = useBasketLocked()

  const { basketLoading, basketAllRefetcher, setShippingIds, shippingIds, profile } =
    useContext(ProfileContext)

  const { removeProductBasket, loadingDelete, deletingItemIds } = useBasketHelper()

  const { formatMessage } = useIntl()

  const shippingGroups = SHIPPING_MANAGER.list()

  const queryClient = useQueryClient()
  const invalidateBasket = () =>
    queryClient.invalidateQueries({ queryKey: [ApiQueryEndpoint.BASKET.GET_BASKET.endpoint] })

  const { mutateAsync: updateDateClose } = useRestMutation<any, Error, any>({
    ...ApiMutationEndpoint.BASKET.UPDATE_PRODUCT,
    onSuccess: invalidateBasket,
  })

  const { mutateAsync: updateProductBasket } = useRestMutation<any, Error, any>({
    ...ApiMutationEndpoint.BASKET.UPDATE_PRODUCT,
    onSuccess: invalidateBasket,
  })

  const debouncedSetQtyCart = React.useMemo(
    () =>
      debounce((rowSelected: ProductsBasket, newQty: number) => {
        const qtyInit = Number(rowSelected.amount)
        return updateProductBasket({
          item_id: rowSelected.item_id,
          product_id: rowSelected.product_id,
          amount: newQty,
          company_id: rowSelected.company_id,
        })
          .then(() => {
            gaAddToCart(rowSelected, newQty - qtyInit)
            basketAllRefetcher()
            toast.success(t({ id: 'web_quantite_mis_a_jour' }))
          })
          .catch(e => {
            logger.error(e)
          })
      }, 1000),
    [updateProductBasket, basketAllRefetcher]
  )

  const handleChangeShippingImplodeDateClose = async group => {
    confirm({
      description: 'Je recevrai ma commande quand la totalité des produits seront disponibles.',
    }).then(() => {
      updateDateClose({
        company_id: String(group.company_id),
        mode: 'GROUP',
        item_id: '',
      })
        .then(() => {
          basketAllRefetcher()
        })
        .catch(e => {
          toast.error(e.message)
        })
    })
  }

  const handleChangeShipping = async event => {
    // `shippingIds` (state de useBasket) reste a null tant que le hook n'a pas eu BESOIN de
    // resynchroniser : il court-circuite quand le localStorage est deja a jour, et n'appelle
    // alors jamais setShippingIds. La source de verite est donc le localStorage, pas le state.
    const currentShippings = shippingIds ?? SHIPPING_MANAGER.list() ?? []
    const shippingGroups = currentShippings.filter(ship => ship.group_key != group.group_key)

    shippingGroups.push({
      shipping_group_key: group.shippings?.[0]?.group_key,
      group_key: group.group_key,
      company_id: group.company_id,
      shipping_id: event.target.value,
    })

    setShippingIds(shippingGroups)

    SHIPPING_MANAGER._set(shippingGroups)
  }

  if (basketLoading) return <MainLoader />
  const group_products = group.products
    .filter(removeDisplayFraisDePrepraProduct)
    .filter((p: any) => {
      // Hide locked replace par defaut. Toggle "Vue bon commande" via context.
      const isLocked =
        (p as any)?.is_replace_locked ||
        (typeof p?.item_id === 'string' && p.item_id.startsWith('rpl_')) ||
        p?.extra?._replace_locked
      return showLocked || !isLocked
    })

  const columns = [
    {
      title: '',
      className: 'w-auto !border-none !p-0',
    },
    {
      title: '',
      className: 'w-[400px] !border-none !p-0',
    },
    {
      title: '',
      className: 'w-24 !border-none !p-0',
    },
    {
      title: '',
      className: 'w-4 !border-none !p-0',
    },
  ]

  // ---
  // Calcul des indication pour le franco de port
  // ---

  const { totalPrice, totalPriceWithoutDiscountCode, amountFranco, shippingSelected } =
    dataShippingFranco(group, shippingGroups)

  // Minimum d'achat de la boutique : montant manquant décidé par l'API (0 = commandable)
  const minOrderPriceMissing = Number(group.min_order_price_missing) || 0

  const minOrderPrice = group.min_order_price

  const data = group_products
    .sort((a, b) => {
      if (!a.planning_shipping_current?.date_close && b.planning_shipping_current?.date_close)
        return -1
      if (!!a.planning_shipping_current?.date_close && !b.planning_shipping_current?.date_close)
        return 1
    })
    .map((row: ProductsBasket) => {
      const productDeclination = getProductDeclination(row)

      const urlProduct = getRoutes({ route: 'Product', values: { '[slug]': row.seo_name } })

      const lineProduct = [
        {
          gridXs: 12,
          value: (
            <div className="flex flex-row items-center w-full">
              {!compactMode && (
                <div className="pr-4">
                  <Link href={urlProduct.href} as={urlProduct.as}>
                    <Paper
                      elevation={3}
                      style={{
                        width: sizeMinImageProduct + 16,
                        height: sizeMinImageProduct + 16,
                        padding: 8,
                      }}
                    >
                      <ProductImg product={row} size={'small'} />
                    </Paper>
                  </Link>
                </div>
              )}
              <div className={clsx('flex flex-col w-full', compactMode ? 'p-0' : 'p-2')}>
                {!row ? (
                  <Skeleton
                    variant="rectangular"
                    width={200}
                    height={sizeMinImageProduct}
                    animation={false}
                  />
                ) : (
                  <>
                    <div className="flex items-end gap-2">
                      <BrandNameLink className={compactMode ? '!text-xs' : ''} product={row} />
                      {row.product_code ? `-  EAN : ${row.product_code}` : ''}
                    </div>
                    <div className={classesProduct.productName}>
                      <Link href={urlProduct.href} as={urlProduct.as}>
                        {productDeclination?.productName || row?.product}
                      </Link>
                    </div>
                    {!compactMode && (
                      <div>
                        {row.in_stock && parseInt(row.in_stock) > 0 ? (
                          <> - {row.in_stock} produits en stock </>
                        ) : (
                          ''
                        )}
                        {/*    option_can_receive_immediately -
                      {row.option_can_receive_immediately
                        ? ' - plusieurs livraison possible'
                        : '  - plusieurs livraison non possible'}*/}
                        {(row.option_can_receive_immediately ||
                          row.option_can_receive_last_date) && (
                          <BasketProductPlanningButton
                            product={row}
                            mode={row.option_can_receive_immediately ? 'DEGROUP' : 'GROUP'}
                          />
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          ),
          className: 'text-ellipsis overflow-hidden',
        },
        {
          gridXs: 6,
          value: (row as any).is_replace_locked ? (
            <div className="flex items-center gap-2">
              <span className="font-bold">{row.amount}</span>
              <span
                className="text-xs px-2 py-0.5 rounded"
                style={{ background: '#fff4e5', color: '#663c00', border: '1px solid #ed6c02' }}
                title="Produit issu d'une commande en attente, non modifiable"
              >
                🔒 Cmd #{(row as any).replace_source_order_id}
              </span>
            </div>
          ) : (
            <ProductQtyInput
              product={row}
              initialQty={parseInt(row.amount)}
              onChange={newQty => {
                if (newQty > 0) {
                  debouncedSetQtyCart(row, newQty)
                }
              }}
              showInfoBar={!compactMode}
              classes={{ container: compactMode ? '!my-0.5 !p-0.5 !w-[300px]' : '' }}
            />
          ),
        },
      ]

      lineProduct.push({
        gridXs: 4,
        value: (
          <span className="w-24 whitespace-nowrap">
            {n(Number(row?.original_price) * Number(row.amount))} HT
          </span>
        ),
      })

      lineProduct.push({
        gridXs: 2,
        // Ligne en cours de suppression (ou "supprimer tout vendeur") :
        // loader a la place des boutons
        value: deletingItemIds.includes(String(row.item_id)) ? (
          <div className="flex flex-col items-center">
            <CircularProgress size={20} color="error" />
          </div>
        ) : (row as any).is_replace_locked ? (
          <div className="flex flex-col items-center gap-2">
            <Tooltip title="Produit verrouille (commande en attente paiement). Non supprimable.">
              <DeleteForeverOutlined className="text-gray-300 cursor-not-allowed" />
            </Tooltip>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-4">
            <Tooltip title={formatMessage({ id: 'web_supprimer_produit' })}>
              <DeleteForeverOutlined
                onClick={() => (loadingDelete ? null : removeProductBasket([row]))}
                className="text-red-400 cursor-pointer hover:text-red-700"
              />
            </Tooltip>
            <CartSaveForLater product={row} />
          </div>
        ),
      })

      return lineProduct
    })

  // Skip render group entier si tous produits masques par filter showLocked.
  if (group_products.length === 0) {
    return null
  }

  return (
    <>
      <style>
        {`
        .border-error-in-basket:has([data-error="true"]) {
          border-color:rgb(255, 154, 154) !important;
          border-width: 1px !important;
          border-style: solid !important;
        }
      `}
      </style>
      <div
        id={`group_${group.company_id}`}
        className={clsx('w-full ', !compactMode && 'mt-4 mb-4')}
      >
        <Card
          elevate
          smallRadius
          className={clsx(
            'border-error-in-basket',
            compactMode && '!p-0 rounded-md border-gray-100 border-solid'
          )}
        >
          <div
            className={clsx(
              'lg:flex lg:justify-between lg:items-center bg-blue-50 pl-4 pr-2  pt-2  pb-2',
              !compactMode && '-mx-4 -mt-4 !p-4 !pr-8'
            )}
          >
            <div className="flex items-center space-x-2">
              {compactModeParent && (
                <div className="text-gray-500">
                  {compactMode ? (
                    <Tooltip title="Déplier">
                      <OpenInFullIcon
                        onClick={() => setLocalCompactMode(!compactMode)}
                        className="cursor-pointer w-2 h-2"
                      />
                    </Tooltip>
                  ) : (
                    <Tooltip title="Replier">
                      <CloseFullscreenIcon
                        onClick={() => setLocalCompactMode(!compactMode)}
                        className="cursor-pointer w-2 h-2"
                      />
                    </Tooltip>
                  )}
                </div>
              )}

              <CompanyName
                object={{
                  id: group.company_id,
                  name: group.name,
                  company_seo_name: group.company_seo_name,
                }}
              />

              {!profile?.groupAeliaStore && !profile?.groupOutreMer && (
                <div className="justify-self-center">
                  {shippingSelected?.shipping_id && group.shippings.length ? (
                    <Select
                      value={shippingSelected.shipping_id}
                      onChange={handleChangeShipping}
                      label={''}
                      variant={'outlined'}
                      style={{ height: 25 }}
                    >
                      <MenuItem value="" disabled>
                        Livraison
                      </MenuItem>
                      {group.shippings?.map(shipping => (
                        <MenuItem key={shipping.shipping_id} value={shipping.shipping_id}>
                          {`${shipping.shipping} - ${n(shipping?.rate)}`}
                        </MenuItem>
                      ))}
                    </Select>
                  ) : (
                    <div style={{ color: COLORS.error }}>
                      <div
                        dangerouslySetInnerHTML={{
                          __html: t({ id: 'text_no_shipping_methods' }),
                        }}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2 lg:mt-0 lg:flex justify-between items-end lg:space-x-4">
              {group?.payment_offline == 'Y' && (
                <div className="justify-self-center">
                  <span className="text-[8px] text-white bg-slate-500 font-semibold rounded-md px-2 py-1 whitespace-nowrap">
                    Paiement via LCR
                  </span>
                </div>
              )}

              {group?.is_precommande && (
                <div className="justify-self-center flex flex-col items-center gap-0.5">
                  <span className="text-[8px] text-white bg-amber-600 font-semibold rounded-md px-2 py-1 whitespace-normal lg:whitespace-nowrap text-center uppercase tracking-wide">
                    Précommande
                    {group.date_close_order
                      ? ` • Livraison le ${new Date(group.date_close_order * 1000).toLocaleDateString('fr-FR')}`
                      : ''}
                  </span>
                  {group.date_close_order && (
                    <span className="text-[9px] text-amber-700 italic whitespace-normal lg:whitespace-nowrap text-center">
                      Paiement SEPA le{' '}
                      {new Date(
                        group.date_close_order * 1000 + 30 * 24 * 3600 * 1000
                      ).toLocaleDateString('fr-FR')}
                    </span>
                  )}
                </div>
              )}

              {minOrderPriceMissing > 0 && (
                <div data-error="true" className="justify-self-center">
                  <MinOrderPriceProgress
                    minOrderPriceLimit={minOrderPrice}
                    missingAmount={minOrderPriceMissing}
                  />
                </div>
              )}

              <div className="justify-self-center">
                <FrancoProgress
                  amountFranco={amountFranco}
                  totalPrice={totalPrice}
                  group={group}
                  shippingSelected={shippingSelected}
                />
              </div>

              <Tooltip title="Total pour ce vendeur">
                <div className="justify-self-center whitespace-nowrap font-semibold cursor-help lg:w-16">
                  {n(totalPriceWithoutDiscountCode)}
                  {totalPrice != totalPriceWithoutDiscountCode && (
                    <div className={'text-[9px] text-gray-500 italic'}>{n(totalPrice)}</div>
                  )}
                </div>
              </Tooltip>

              <Tooltip title="Supprimer tous les produits de ce vendeur">
                <div
                  onClick={() => (loadingDelete ? null : removeProductBasket(group_products))}
                  className="cursor-pointer"
                >
                  {loadingDelete ? (
                    <CircularProgress size={20} color="error" />
                  ) : (
                    <DeleteForeverOutlined color="error" />
                  )}
                </div>
              </Tooltip>
            </div>
          </div>

          <div>
            <TableResponsive
              columns={columns}
              data={data}
              classNameTable="min-w-[550px]"
              rowClassName={clsx(
                'odd:bg-white hover:!bg-blue-50 rounded md:rounded-none px-2 py-2 md:px-0 md:py-0 my-2 md:my-0 even:bg-gray-50'
              )}
              cellClassName={clsx(
                'bg-transparent group-hover:!bg-gray-50',
                compactMode && '!py-0.5 !px-2 !pr-2 !border-none'
              )}
            />
          </div>

          <BasketFrancoSuggestions
            companyId={group.company_id}
            productIds={Object.values(group.products ?? {}).map((product: any) => product.product_id)}
            amountFranco={amountFranco}
            totalPrice={totalPrice}
          />

          <div className="flex justify-end items-center">
            <Grid container spacing={0}>
              <Grid item md={10} xs={12} className="flex justify-end items-center">
                <span>
                  {group.option_can_receive_all_products_last_date && (
                    <Button
                      onClick={() => handleChangeShippingImplodeDateClose(group)}
                      variant="outlined"
                      style={{
                        borderColor: COLORS.greenDark,
                        color: COLORS.greenDark,
                        whiteSpace: 'nowrap',
                      }}
                      size="small"
                    >
                      Je souhaite recevoir mes produits en un seul colis
                    </Button>
                  )}
                </span>
              </Grid>
            </Grid>
          </div>
        </Card>
      </div>
    </>
  )
}

export default BasketDetailCompany
